<script>
import Drawer from "@/javascript/views/components/Drawer.vue";
import { useHouseholdStore } from "@/javascript/stores/household";
import { useOrganizationStore } from "@/javascript/stores/organizations";
import { useAnalyticEventStore } from "@/javascript/stores/analyticEvents";
import Icon from "../../components/Icon.vue";

export default {
  components: { Drawer, Icon },
  emits: ['created'],
  setup() {
    const householdStore = useHouseholdStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();
    return { householdStore, organizationStore, analyticEventStore };
  },
  data() {
    return {
      form: {
        name: '',
        address_street: '',
        address_city: '',
        address_state: '',
        address_postal_code: '',
        address_country: ''
      },
      members: [],
      donorSearch: '',
      donorResults: [],
      searching: false,
      newMemberRole: 'Other',
      roles: ['Head of Household', 'Spouse', 'Child', 'Dependent', 'Other']
    };
  },
  methods: {
    show() {
      this.form = { name: '', address_street: '', address_city: '', address_state: '', address_postal_code: '', address_country: '' };
      this.members = [];
      this.$refs.drawer.show();
    },
    hide() {
      this.$refs.drawer.hide();
    },
    async searchDonors() {
      if (this.donorSearch.length < 2) {
        this.donorResults = [];
        return;
      }
      this.searching = true;
      const currentTerm = this.donorSearch;
      const params = new URLSearchParams();
      params.set('query[user_first_name_or_user_last_name_or_user_email_cont]', this.donorSearch);
      params.set('query[organization_id_eq]', this.organizationStore.organizationId);
      params.set('per', '10');
      const url = `/organizations/${this.organizationStore.organizationId}/donors`;
      const res = await fetch(`${url}?${params.toString()}`, {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const json = await res.json();
      if (this.donorSearch === currentTerm) {
        this.donorResults = json.data || [];
      }
      this.searching = false;
    },
    addMember(donor) {
      if (this.members.find(m => m.user_id === donor.user_id)) return;
      if (donor.household && !confirm(`This donor is in "${donor.household.name}". They will be moved to this household. Continue?`)) return;
      this.members.push({
        user_id: donor.user_id,
        name: donor.user_details?.f_name + ' ' + donor.user_details?.l_name,
        email: donor.user_details?.email,
        role: this.newMemberRole
      });
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'household-member-added' });
      this.donorSearch = '';
      this.donorResults = [];
    },
    removeMember(index) {
      if(this.members.length <= 1){
        alert("Household must have at least one member");
        return;
      }
      if (confirm("Remove this member from the household?")) {
        this.members.splice(index, 1);
        this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
        this.analyticEventStore.logEvent({ action: 'household-member-removed' });
      }
    },
    async save() {
      if (!this.form.name || this.members.length === 0) {
        alert('Household name and at least one member are required.');
        return;
      }
      const params = {
        ...this.form,
        members: this.members.map(m => ({ user_id: m.user_id, role: m.role }))
      };
      const response = await this.householdStore.create(params);
      if (response.status === 422) {
        // Error Handling
        const messages = Object.values(response.json.errors).flat();
        alert(messages.join('\n'));
        return;
      }
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'household-created' });
      this.$emit('created');
      this.householdStore.getHouseholds();
      this.hide();
    }
  }
};
</script>

<template>
  <Drawer
    ref="drawer"
    size="large"
  >
    <div class="p-6 space-y-6 overflow-y-auto h-screen">
      <h2 class="text-xl font-bold">
        Create Household
      </h2>

      <!-- Name -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Household Name *</label>
        <input
          v-model="form.name"
          type="text"
          class="w-full border border-gray-300 rounded-lg px-3 py-2"
          placeholder="e.g. The Smith Family"
        >
      </div>

      <!-- Members -->
      <div>
        <h3 class="text-sm font-semibold text-gray-600 mb-2">
          Members *
        </h3>
        <div
          v-for="(member, i) in members"
          :key="i"
          class="flex items-center gap-3 mb-2 border rounded-lg p-3"
        >
          <div class="flex-1">
            <p class="font-medium text-sm">
              {{ member.name }}
            </p>
            <p class="text-xs text-gray-400">
              {{ member.email }}
            </p>
          </div>
          <select
            v-model="member.role"
            class="text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option
              v-for="role in roles"
              :key="role"
              :value="role"
            >
              {{ role }}
            </option>
          </select>
          <Icon
            icon="trash"
            color="#ef4444"
            class="cursor-pointer"
            @click="removeMember(i)"
          />
        </div>

        <!-- Donor Search -->
        <div class="mt-3">
          <input
            v-model="donorSearch"
            placeholder="Search donors by name or email..."
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            @input="searchDonors"
          >
          <div
            v-if="donorResults.length"
            class="border rounded-lg mt-1 max-h-40 overflow-y-auto"
          >
            <div
              v-for="donor in donorResults"
              :key="donor.id"
              :class="['px-3 py-2 text-sm border-b last:border-b-0 hover:bg-gray-50 cursor-pointer']"
              @click="addMember(donor)"
            >
              <div class="flex items-center gap-2">
                <span>{{ donor.user_details?.f_name }} {{ donor.user_details?.l_name }} — {{ donor.user_details?.email }}</span>
                <span
                  v-if="donor.household"
                  class="text-xs text-red-500 border border-red-300 rounded px-1"
                >In another household</span>
              </div>
              <p
                v-if="donor.household"
                class="text-xs text-gray-400 mt-1"
              >
                This donor is already part of another household and cannot be added here.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Address (optional) -->
      <div class="space-y-3">
        <h3 class="text-sm font-semibold text-gray-600">
          Mailing Address (optional)
        </h3>
        <input
          v-model="form.address_street"
          placeholder="Street"
          class="w-full border border-gray-300 rounded-lg px-3 py-2"
        >
        <div class="grid grid-cols-2 gap-3">
          <input
            v-model="form.address_city"
            placeholder="City"
            class="border border-gray-300 rounded-lg px-3 py-2"
          >
          <input
            v-model="form.address_state"
            placeholder="State/Province"
            class="border border-gray-300 rounded-lg px-3 py-2"
          >
        </div>
        <div class="grid grid-cols-2 gap-3">
          <input
            v-model="form.address_postal_code"
            placeholder="Postal Code"
            class="border border-gray-300 rounded-lg px-3 py-2"
          >
          <input
            v-model="form.address_country"
            placeholder="Country"
            class="border border-gray-300 rounded-lg px-3 py-2"
          >
        </div>
      </div>

      <!-- Save -->
      <div class="flex gap-3 pt-4">
        <button
          class="btn btn-primary px-6 py-2"
          @click="save"
        >
          Create Household
        </button>
        <button
          class="btn btn-outline px-6 py-2"
          @click="hide"
        >
          Cancel
        </button>
      </div>
    </div>
  </Drawer>
</template>
